// `.writingsuite` packages (ProjectPackage.swift, the Apple spec's §4.4), built in the browser from the decrypted
// documents: only the browser can open them, so only the browser can export. A package is a folder:
//
//     manifest.json                    formatVersion, projectID, title, exportedAt, appVersion, fragmentCount,
//                                      checkpointCount, and a SHA-256 (hex) for every file below
//     project.automerge                the project document, saved with its whole history
//     fragments/<id>.automerge         each fragment the project lists that this browser has
//     attachments/<sha256>             attachments, when there are any (the web holds none yet)
//
// The plain-text mirror the Mac adds is left out: the Mac doesn't need it to import. Several packages travel as one zip
// (fflate, synchronous: its asynchronous API starts blob: workers, which the page's policy forbids), each package a
// `<Title>.writingsuite/` folder in it, which the Mac opens as a package once unzipped.
//
// `verifyPackage` is the Mac's own check ported (`ProjectPackage.manifest(of:verify:)` and what `importPackage` reads
// before it merges), so a test can hold an export to what the Mac accepts.
import * as A from '@automerge/automerge';
import { Zip, ZipDeflate, unzipSync } from 'fflate';
import { readFragment, readProject } from '@writing-suite/core';

/** `ProjectPackage.formatVersion`. */
export const FORMAT_VERSION = 1;
export const PACKAGE_EXTENSION = 'writingsuite';

export interface Manifest {
  formatVersion: number;
  projectID: string;
  title: string;
  /** ISO 8601 without fractional seconds, as Swift's `.iso8601` date strategy reads and writes it. */
  exportedAt: string;
  appVersion: string;
  /** Relative path → SHA-256 (lowercase hex) of every document and attachment. */
  files: Record<string, string>;
  fragmentCount: number;
  checkpointCount: number;
}

export interface Package {
  /** The folder's name without the extension (`MirrorWriter.safeFilename(title)`). */
  name: string;
  manifest: Manifest;
  /** Relative path → bytes, `manifest.json` included. */
  files: Map<string, Uint8Array>;
}

export type Bytes = Uint8Array<ArrayBuffer>;

const hex = (b: ArrayBuffer) => [...new Uint8Array(b)].map((x) => x.toString(16).padStart(2, '0')).join('');

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  return hex(await crypto.subtle.digest('SHA-256', bytes as Bytes));
}

/** Swift's `.iso8601` (ISO8601DateFormatter's internet date-time): whole seconds, UTC. */
export const isoSeconds = (d: Date) => `${d.toISOString().slice(0, 19)}Z`;

/** `MirrorWriter.safeFilename`: no path separators or control characters, no leading dots, at most 100 UTF-8 bytes. */
export function safeFilename(title: string): string {
  let cleaned = [...title]
    .map((ch) => (ch === '/' || ch === ':' || ch === '\\' ? '-' : /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u.test(ch) ? ' ' : ch))
    .join('')
    .trim();
  const dots = /^\.*/.exec(cleaned)![0].length;
  cleaned = '-'.repeat(dots) + cleaned.slice(dots);
  const enc = new TextEncoder();
  let capped = '';
  let bytes = 0;
  for (const ch of new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(cleaned)) {
    const n = enc.encode(ch.segment).length;
    if (bytes + n > 100) break;
    capped += ch.segment;
    bytes += n;
  }
  capped = capped.trim();
  return capped || 'Untitled';
}

/** Pretty-printed with sorted keys, as the Swift's encoder writes it (any key order reads the same). */
function manifestJSON(m: Manifest): string {
  const sorted = (v: unknown): unknown =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.keys(v as object).sort().map((k) => [k, sorted((v as Record<string, unknown>)[k])]))
      : v;
  return JSON.stringify(sorted(m), null, 2);
}

export interface BuildOptions {
  exportedAt?: Date;
  appVersion?: string;
  /** Attachments by SHA-256 (hex): `attachments/<sha>`. */
  attachments?: Map<string, Uint8Array>;
}

/**
 * A project's package from its documents (`project`, `fragment.<id>`, as the relay and the replica name them), each
 * saved with its whole history. The manifest names the project by its document's own id (what the Mac checks against
 * `project.automerge`), which for a copy on the web may differ from the relay's id.
 */
export async function buildPackage(docs: Map<string, A.Doc<unknown>>, o: BuildOptions = {}): Promise<Package> {
  const projectDoc = docs.get('project');
  if (!projectDoc) throw new Error('This project has no project document on this computer yet, so it can’t be exported.');
  const record = readProject(projectDoc);
  if (!record.id) throw new Error('This project’s document has no id, so the Mac couldn’t import it.');
  const files = new Map<string, Uint8Array>();
  const hashes: Record<string, string> = {};
  const add = async (path: string, bytes: Uint8Array) => {
    files.set(path, bytes);
    hashes[path] = await sha256Hex(bytes);
  };
  await add('project.automerge', A.save(projectDoc));
  // The fragments the project lists (`fragmentEntries`), each one this browser has (the Mac skips missing files too).
  const fragmentIds = [...record.fragments.keys()].sort();
  for (const id of fragmentIds) {
    const doc = docs.get(`fragment.${id}`);
    if (doc) await add(`fragments/${id}.automerge`, A.save(doc));
  }
  for (const [sha, bytes] of [...(o.attachments ?? new Map<string, Uint8Array>())].sort(([a], [b]) => a.localeCompare(b))) {
    if (/^[0-9a-f]{64}$/.test(sha)) await add(`attachments/${sha}`, bytes);
  }
  const manifest: Manifest = {
    formatVersion: FORMAT_VERSION,
    projectID: record.id,
    title: record.title,
    exportedAt: isoSeconds(o.exportedAt ?? new Date()),
    appVersion: o.appVersion ?? 'web',
    files: hashes,
    fragmentCount: fragmentIds.length,
    checkpointCount: record.checkpoints.length,
  };
  files.set('manifest.json', new TextEncoder().encode(manifestJSON(manifest)));
  return { name: safeFilename(record.title), manifest, files };
}

/** Folder names for packages in one zip: a second "Notes" becomes "Notes 2", as Finder would. */
export function packageFolders(packages: Pick<Package, 'name'>[]): string[] {
  const used = new Set<string>();
  return packages.map((p) => {
    let name = p.name;
    for (let n = 2; used.has(name.toLowerCase()); n++) name = `${p.name} ${n}`;
    used.add(name.toLowerCase());
    return `${name}.${PACKAGE_EXTENSION}`;
  });
}

/**
 * Packages zipped, each as its folder. Synchronous (fflate's `Zip` with `ZipDeflate`), file by file, calling
 * `onFile` after each so a caller can show progress and yield between packages.
 */
export function zipPackages(packages: Package[], onFile?: (done: number, total: number) => void): Uint8Array {
  const chunks: Uint8Array[] = [];
  let failure: Error | null = null;
  const zip = new Zip((err, chunk) => {
    if (err) failure = err;
    else chunks.push(chunk);
  });
  const folders = packageFolders(packages);
  const total = packages.reduce((n, p) => n + p.files.size, 0);
  let done = 0;
  const mtime = new Date();
  packages.forEach((p, i) => {
    // manifest.json first, then the documents in path order.
    const paths = [...p.files.keys()].sort((a, b) => (a === 'manifest.json' ? -1 : b === 'manifest.json' ? 1 : a.localeCompare(b)));
    for (const path of paths) {
      const entry = new ZipDeflate(`${folders[i]}/${path}`, { level: 6 });
      entry.mtime = mtime;
      zip.add(entry);
      entry.push(p.files.get(path)!, true);
      onFile?.(++done, total);
    }
  });
  zip.end();
  if (failure) throw failure;
  const out = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0));
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

/** A zip's packages: folder name (with its extension) → relative path → bytes. Files outside a package are ignored. */
export function unzipPackages(zip: Uint8Array): Map<string, Map<string, Uint8Array>> {
  const out = new Map<string, Map<string, Uint8Array>>();
  for (const [path, bytes] of Object.entries(unzipSync(zip))) {
    const cut = path.indexOf('/');
    if (cut < 0 || path.endsWith('/')) continue;
    const folder = path.slice(0, cut);
    if (!folder.endsWith(`.${PACKAGE_EXTENSION}`)) continue;
    let files = out.get(folder);
    if (!files) out.set(folder, (files = new Map()));
    files.set(path.slice(cut + 1), bytes);
  }
  return out;
}

// The Mac's checks --------------------------------------------------------------------------------------------------

/** `ProjectPackage.PackageError`, with the Mac's messages. */
export class PackageError extends Error {
  constructor(
    readonly kind: 'notAPackage' | 'newerFormat' | 'damaged',
    message: string,
    readonly files: string[] = [],
  ) {
    super(message);
  }
}

const SWIFT_ISO8601 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:Z|[+-]\d{2}:?\d{2})$/;

/** What Swift's `JSONDecoder` (`.iso8601` dates) makes of manifest.json: the `Manifest` it decodes, or why not. */
function decodeManifest(bytes: Uint8Array): Manifest {
  let raw: unknown;
  try {
    raw = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  } catch {
    throw new PackageError('notAPackage', 'This isn’t a Five Rooms package: unreadable manifest');
  }
  const m = raw as Record<string, unknown>;
  const int = (v: unknown) => typeof v === 'number' && Number.isInteger(v);
  const ok =
    !!m && typeof m === 'object' && !Array.isArray(m) &&
    int(m.formatVersion) && typeof m.projectID === 'string' && typeof m.title === 'string' &&
    typeof m.exportedAt === 'string' && SWIFT_ISO8601.test(m.exportedAt) && !Number.isNaN(Date.parse(m.exportedAt)) &&
    typeof m.appVersion === 'string' && int(m.fragmentCount) && int(m.checkpointCount) &&
    !!m.files && typeof m.files === 'object' && !Array.isArray(m.files) &&
    Object.values(m.files as object).every((h) => typeof h === 'string');
  if (!ok) throw new PackageError('notAPackage', 'This isn’t a Five Rooms package: unreadable manifest');
  return m as unknown as Manifest;
}

/**
 * The Mac's checks on a package, in its order: `manifest(of:verify: true)` (a readable manifest, a format it knows,
 * every listed file present with its SHA-256), then what `importPackage` reads before merging anything: the project
 * document loads and has the manifest's id, and each fragment and attachment it lists loads as one. Returns the
 * manifest; throws `PackageError` with the Mac's message.
 */
export async function verifyPackage(files: Map<string, Uint8Array>): Promise<Manifest> {
  const manifestBytes = files.get('manifest.json');
  if (!manifestBytes) throw new PackageError('notAPackage', 'This isn’t a Five Rooms package: no manifest.json');
  const manifest = decodeManifest(manifestBytes);
  if (manifest.formatVersion > FORMAT_VERSION) {
    throw new PackageError('newerFormat', `This package was made by a newer version of Five Rooms (format ${manifest.formatVersion}).`);
  }
  const bad: string[] = [];
  for (const [path, hash] of Object.entries(manifest.files)) {
    const bytes = files.get(path);
    if (!bytes || (await sha256Hex(bytes)) !== hash) bad.push(path);
  }
  bad.sort();
  if (bad.length) throw new PackageError('damaged', `The package is damaged; these files don’t match their checksums: ${bad.join(', ')}`, bad);

  // importPackage: `ProjectDocument(data:)` and its id against the manifest's.
  let project: A.Doc<unknown>;
  try {
    project = A.load(files.get('project.automerge') ?? new Uint8Array());
  } catch {
    throw new PackageError('notAPackage', 'This isn’t a Five Rooms package: the project document doesn’t load');
  }
  if (readProject(project).id !== manifest.projectID) {
    throw new PackageError('notAPackage', 'This isn’t a Five Rooms package: the project document doesn’t match the manifest');
  }
  for (const path of Object.keys(manifest.files).sort()) {
    if (path.startsWith('fragments/')) {
      let id = '';
      try {
        id = readFragment(A.load(files.get(path)!)).id;
      } catch {
        // below
      }
      if (!id) throw new PackageError('notAPackage', `This isn’t a Five Rooms package: ${path} isn’t a fragment document`);
    } else if (path.startsWith('attachments/')) {
      // `store.storeAttachment` keys it by its own SHA-256, which the manifest's checksum has just confirmed.
      if (!/^attachments\/[0-9a-f]{64}$/.test(path) || path.slice('attachments/'.length) !== manifest.files[path]) {
        throw new PackageError('notAPackage', `This isn’t a Five Rooms package: ${path} isn’t named by its SHA-256`);
      }
    }
  }
  return manifest;
}
